import { useCallback, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Upload, FileText, CheckCircle, RefreshCw, ArrowRight, XCircle } from 'lucide-react'
import { engineApiClient } from '@/lib/apiClient'
import type { DocumentRecord } from '@/lib/contracts'

const FALLBACK_PROJECT_ID = 'alpha-grid'

const BEST_PRACTICES = [
  'Upload the full JD — even a draft works',
  'Include hiring manager notes if available',
  'Org charts help Erica understand reporting structure',
  'Company overview sets market context',
  "Don't worry about formatting — AI handles extraction",
]

const KIND_LABELS: Record<DocumentRecord['kind'], string> = {
  'jd': 'JD',
  'org-chart': 'Org Chart',
  'notes': 'Notes',
  'other': 'Other',
}

const statusConfig: Record<string, { label: string; class: string; spin?: boolean }> = {
  processed:  { label: 'Ready',      class: 'bg-green-500/10 text-green-400' },
  processing: { label: 'Processing', class: 'bg-[#FD802E]/10 text-[#FD802E]', spin: true },
  ready:      { label: 'Uploaded',   class: 'bg-blue-500/10 text-blue-400' },
  error:      { label: 'Error',      class: 'bg-red-500/10 text-red-400' },
}

interface UploadingFile {
  name: string
  status: 'uploading' | 'error'
  error?: string
}

const inferKind = (filename: string): DocumentRecord['kind'] => {
  const lower = filename.toLowerCase()
  if (lower.includes('jd') || lower.includes('job') || lower.includes('description') || lower.includes('role')) return 'jd'
  if (lower.includes('org') || lower.includes('chart') || lower.includes('structure')) return 'org-chart'
  if (lower.includes('note') || lower.includes('brief') || lower.includes('spec')) return 'notes'
  return 'other'
}

export const UploadDocuments = () => {
  const { id } = useParams<{ id: string }>()
  const projectId = id ?? FALLBACK_PROJECT_ID
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [docs, setDocs] = useState<DocumentRecord[]>([])
  const [uploading, setUploading] = useState<UploadingFile[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isLoadingDocs, setIsLoadingDocs] = useState(true)

  // Load existing documents on mount
  const loadDocs = useCallback(async () => {
    try {
      setIsLoadingDocs(true)
      const response = await engineApiClient.listDocuments(projectId)
      setDocs(response.documents)
    } catch {
      setLoadError('Failed to load documents')
    } finally {
      setIsLoadingDocs(false)
    }
  }, [projectId])

  // Call loadDocs once on mount
  useState(() => { loadDocs() })

  const uploadFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files)
    if (fileArray.length === 0) return

    // Add optimistic "uploading" entries
    const newUploading: UploadingFile[] = fileArray.map((f) => ({ name: f.name, status: 'uploading' }))
    setUploading((prev) => [...prev, ...newUploading])

    for (const file of fileArray) {
      try {
        const kind = inferKind(file.name)
        const response = await engineApiClient.uploadDocumentFile(projectId, file, kind)
        setDocs(response.documents)
        // Remove from uploading on success
        setUploading((prev) => prev.filter((u) => u.name !== file.name))
      } catch (err) {
        setUploading((prev) =>
          prev.map((u) =>
            u.name === file.name
              ? { ...u, status: 'error', error: err instanceof Error ? err.message : 'Upload failed' }
              : u
          )
        )
      }
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      void uploadFiles(e.target.files)
      // Reset input so the same file can be re-uploaded if needed
      e.target.value = ''
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files) {
      void uploadFiles(e.dataTransfer.files)
    }
  }

  const readyCount = docs.filter((d) => d.status === 'processed' || d.status === 'ready').length
  const processingCount = docs.filter((d) => d.status === 'processing').length
  const allDocs: (DocumentRecord | UploadingFile)[] = [
    ...uploading,
    ...docs,
  ]

  return (
    <div className="bg-[#02182B]">
      {/* Page header */}
      <div className="border-b border-[#233D4C]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-2xl font-bold text-white">Upload Documents</h1>
          <p className="text-gray-400 text-sm mt-1">Upload JDs, org charts, hiring manager notes, and briefs</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col lg:flex-row gap-8">

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-6">

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer ${
              isDragging ? 'border-[#FD802E] bg-[#FD802E]/10' : 'border-[#FD802E]/30 bg-[#FD802E]/5 hover:border-[#FD802E]/60 hover:bg-[#FD802E]/10'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="w-14 h-14 bg-[#FD802E]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Upload size={26} className="text-[#FD802E]" />
            </div>
            <p className="text-white font-semibold text-lg">Drop files here or click to upload</p>
            <p className="text-gray-400 text-sm mt-1">Upload JDs, org charts, hiring manager notes, and briefs</p>
            <p className="text-gray-500 text-xs mt-1">Supported: PDF, DOCX, TXT, MD</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept=".pdf,.docx,.txt,.md"
              onChange={handleFileInput}
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click() }}
              className="mt-6 bg-[#FD802E] text-[#233D4C] font-semibold px-6 py-2.5 rounded-lg hover:bg-[#ff8f45] transition-colors text-sm"
            >
              Upload Files
            </button>
          </div>

          {/* File list */}
          {loadError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm">{loadError}</div>
          )}

          {(isLoadingDocs || allDocs.length > 0) && (
            <div className="space-y-3">
              <h3 className="text-white font-semibold text-sm">Uploaded Files</h3>
              {isLoadingDocs && allDocs.length === 0 ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 animate-pulse h-16" />
                  ))}
                </div>
              ) : (
                <ul className="space-y-2">
                  {/* Uploading files (optimistic) */}
                  {uploading.map((u) => (
                    <li key={`uploading-${u.name}`} className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-4 py-3 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 bg-[#FD802E]/10 rounded-lg flex items-center justify-center shrink-0">
                          <FileText size={14} className="text-[#FD802E]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-white text-sm font-medium truncate">{u.name}</p>
                          {u.error && <p className="text-red-400 text-xs">{u.error}</p>}
                        </div>
                      </div>
                      <span className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        u.status === 'error' ? 'bg-red-500/10 text-red-400' : 'bg-[#FD802E]/10 text-[#FD802E]'
                      }`}>
                        {u.status === 'error'
                          ? <><XCircle size={11} /> Error</>
                          : <><RefreshCw size={11} className="animate-spin" /> Uploading</>
                        }
                      </span>
                    </li>
                  ))}
                  {/* Successfully uploaded files */}
                  {docs.map((doc) => {
                    const cfg = statusConfig[doc.status] ?? { label: doc.status, class: 'bg-gray-500/10 text-gray-400' }
                    return (
                      <li key={doc.id} className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-4 py-3 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 bg-[#FD802E]/10 rounded-lg flex items-center justify-center shrink-0">
                            <FileText size={14} className="text-[#FD802E]" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white text-sm font-medium truncate">{doc.name}</p>
                            <p className="text-gray-400 text-xs">
                              {KIND_LABELS[doc.kind]}
                              {doc.wordCount ? ` · ${doc.wordCount.toLocaleString()} words extracted` : ''}
                            </p>
                          </div>
                        </div>
                        <span className={`shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.class}`}>
                          {cfg.spin
                            ? <RefreshCw size={11} className="animate-spin" />
                            : <CheckCircle size={11} />
                          }
                          {cfg.label}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="w-full lg:w-72 lg:flex-shrink-0 space-y-4">

          {/* Best practices */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-3">
            <p className="text-xs uppercase text-gray-400 font-medium">Best Practices</p>
            <ul className="space-y-2">
              {BEST_PRACTICES.map((tip) => (
                <li key={tip} className="flex items-start gap-2 text-sm">
                  <CheckCircle size={13} className="text-green-400 mt-0.5 shrink-0" />
                  <span className="text-gray-300">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Upload status summary */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-3">
            <p className="text-xs uppercase text-gray-400 font-medium">Upload Status</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Files uploaded</span>
                <span className="text-white font-medium">{docs.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Ready to review</span>
                <span className="text-green-400 font-medium">{readyCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Still processing</span>
                <span className={`font-medium ${processingCount > 0 ? 'text-[#FD802E]' : 'text-gray-500'}`}>
                  {processingCount}
                </span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={() => {
              engineApiClient.advanceProjectStage(projectId, 'intake').catch(() => {})
              navigate(`/project/${projectId}/intake`)
            }}
            disabled={docs.length === 0 && uploading.length === 0}
            className="w-full flex items-center justify-center gap-2 bg-[#FD802E] text-[#233D4C] font-semibold py-3 rounded-lg hover:bg-[#ff8f45] transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Start Conversation <ArrowRight size={15} />
          </button>

          {docs.length === 0 && uploading.length === 0 && (
            <p className="text-center text-gray-500 text-xs">Upload at least one document to continue</p>
          )}
        </div>
      </div>
    </div>
  )
}
